import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import type { QueryKey } from "react-query";
import { clearStoredAuth } from "../utils";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

const fetchFlights = async (params: string) => {
  const res = await fetch(`${API_URL}/flights${params}`, {
    credentials: "include",
  });
  // TODO: process error
  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

const withBeforeDay = (params: string, beforeDay?: string) => {
  if (!beforeDay) {
    return params;
  }

  const searchParams = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
  searchParams.set("beforeDay", beforeDay);
  return `?${searchParams.toString()}`;
};

const useFlights = (params: string) => {
  const navigate = useNavigate();

  return useQuery(["flights", params], () => fetchFlights(params), {
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

export const useInfiniteFlights = (
  params: string,
  queryKey: QueryKey = ["flights", "day", params],
) => {
  const navigate = useNavigate();

  return useInfiniteQuery(
    queryKey,
    ({ pageParam }) => fetchFlights(withBeforeDay(params, pageParam)),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      getNextPageParam: (lastPage) => (
        lastPage?.hasMore && lastPage?.nextCursorDay
          ? lastPage.nextCursorDay
          : undefined
      ),
      onError: (error: Error) => {
        if (error.message === "401") {
          clearStoredAuth();
          navigate("/");
        }
      },
    },
  );
};

const fetchSavedFlightFilters = async () => {
  const res = await fetch(`${API_URL}/flights/saved-filters`, {
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

type SavedFlightFilterPayload = {
  name: string;
  filters: Record<string, unknown> | Partial<Filters>;
  isDefault?: boolean;
  isPinned?: boolean;
};

const saveFlightFilter = async (payload: SavedFlightFilterPayload) => {
  const res = await fetch(`${API_URL}/flights/saved-filters`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

const updateFlightFilter = async (
  payload: SavedFlightFilterPayload & { id: number },
) => {
  const res = await fetch(`${API_URL}/flights/saved-filters/${payload.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

const deleteFlightFilter = async (id: number) => {
  const res = await fetch(`${API_URL}/flights/saved-filters/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }
};

const useUnauthorizedHandler = () => {
  const navigate = useNavigate();

  return (error: Error) => {
    if (error.message === "401") {
      clearStoredAuth();
      navigate("/");
    }
  };
};

export const useSavedFlightFilters = () => {
  const handleUnauthorized = useUnauthorizedHandler();

  return useQuery<{ filters: SavedFlightFilter[] }, Error>(
    ["flights", "saved-filters"],
    fetchSavedFlightFilters,
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: handleUnauthorized,
    },
  );
};

export const useSaveFlightFilter = () => {
  const queryClient = useQueryClient();
  const handleUnauthorized = useUnauthorizedHandler();

  return useMutation(saveFlightFilter, {
    onSuccess: () => {
      queryClient.invalidateQueries(["flights", "saved-filters"]);
    },
    onError: handleUnauthorized,
  });
};

export const useUpdateFlightFilter = () => {
  const queryClient = useQueryClient();
  const handleUnauthorized = useUnauthorizedHandler();

  return useMutation(updateFlightFilter, {
    onSuccess: () => {
      queryClient.invalidateQueries(["flights", "saved-filters"]);
    },
    onError: handleUnauthorized,
  });
};

export const useDeleteFlightFilter = () => {
  const queryClient = useQueryClient();
  const handleUnauthorized = useUnauthorizedHandler();

  return useMutation(deleteFlightFilter, {
    onSuccess: () => {
      queryClient.invalidateQueries(["flights", "saved-filters"]);
    },
    onError: handleUnauthorized,
  });
};

export default useFlights;
